import {Component, Inject} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {AdminService} from '../../services/admin.service';

@Component({
  selector: 'app-match-modal',
  templateUrl: './match-modal.component.html',
  styleUrls: ['./match-modal.component.scss'],
})
export class MatchModalComponent {
  weekNumber!: number;
  matches: any[] = [];
  teams: any[] = [];
  filteredHomeTeams: any[][] = [];
  filteredAwayTeams: any[][] = [];
  isLoading = false;

  constructor(
    public dialogRef: MatDialogRef<MatchModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private adminService: AdminService
  ) {
    this.loadTeams();
    this.loadLastWeekNumber();
  }

  get isSaveDisabled(): boolean {
    const requiredMatches = Math.floor(this.teams.length / 2);
    return (
      this.matches.length < requiredMatches ||
      this.matches.some(match => !match.homeTeamId || !match.awayTeamId || !match.gameDate || !match.gameTime)
    );
  }

  matchStatuses = Object.values(MatchStatus);

  addMatch(): void {
    const newMatch = {
      homeTeamId: '',
      awayTeamId: '',
      homeTeamScore: 0,
      awayTeamScore: 0,
      gameDate: '',
      gameTime: '',
      status: 'UPCOMING',
      leagueId: this.data.leagueId,
    };

    this.matches.push(newMatch);

    this.filteredHomeTeams.push([...this.teams]);
    this.filteredAwayTeams.push([...this.teams]);

    this.updateAvailableTeams();
  }

  save(): void {
    this.isLoading = true;

    const payload = {
      weekNumber: this.weekNumber,
      matches: this.matches.map((match) => {
        let formattedDate: string | null = null;

        if (match.gameDate && match.gameTime) {
          try {
            formattedDate = this.formatDate(match.gameDate, match.gameTime);
          } catch (e) {
            console.error('Invalid date or time value:', match.gameDate, match.gameTime);
          }
        }

        return {
          ...match,
          date: formattedDate,
        };
      }),
    };

    if (payload.matches.some((match) => match.date === null)) {
      console.error('One or more matches have invalid date values.');
      this.isLoading = false;
      return;
    }

    this.adminService
      .addWeekMatches(this.data.leagueId, payload.weekNumber, payload.matches)
      .subscribe(
        () => {
          this.isLoading = false;
          this.dialogRef.close(true);
        },
        (error) => {
          console.error('Error saving matches:', error);
          this.isLoading = false;
        }
      );
  }

  updateAvailableTeams(): void {
    const selectedTeams = new Set(this.matches.flatMap((match) => [match.homeTeamId, match.awayTeamId]).filter(Boolean));

    this.matches.forEach((match, index) => {
      this.filteredHomeTeams[index] = this.teams.filter(
        (team) => !selectedTeams.has(team.id) || team.id === match.homeTeamId
      );

      this.filteredAwayTeams[index] = this.teams.filter(
        (team) => (!selectedTeams.has(team.id) || team.id === match.awayTeamId) && team.id !== match.homeTeamId
      );
    });
  }

  filterAwayTeams(index: number): void {
    this.updateAvailableTeams();
  }

  removeMatch(index: number): void {
    this.matches.splice(index, 1);
    this.filteredHomeTeams.splice(index, 1);
    this.filteredAwayTeams.splice(index, 1);
    this.updateAvailableTeams();
  }

  private loadLastWeekNumber(): void {
    this.adminService.getWeeksPerLeague(this.data.leagueId).subscribe((weeks: any) => {
      if (weeks.length > 0) {
        const lastWeekNumber = Math.max(...weeks);
        this.weekNumber = lastWeekNumber + 1;
      } else {
        this.weekNumber = 1;
      }
    });
  }

  private formatDate(dateString: string, timeString: string): string {
    const dateObj = new Date(dateString);
    let formattedTime = timeString.length === 5 ? `${timeString}:00` : timeString;
    return `${dateObj.getFullYear()}-${(dateObj.getMonth() + 1).toString().padStart(2, '0')}-${dateObj.getDate().toString().padStart(2, '0')} ${formattedTime}`;
  }

  private loadTeams(): void {
    this.adminService.getAllTeamsFromOneLeague(this.data.leagueId).subscribe((response) => {
      this.teams = response.content;
    });
  }
}

export enum MatchStatus {
  UPCOMING = 'UPCOMING',
  FINISHED = 'FINISHED',
  POSTPONED = 'POSTPONED'
}
